import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import isNil from 'lodash/isNil';
import { lazyLoadNoSpinner } from 'utils/lazyLoad';
import { isCreateUserModalOpen } from 'selectors/addUsers';
import { voiceCreditPercentage } from 'selectors/featureToggles';
import { isEventWidgetSoundSettingsShown } from 'selectors/eventsWidgetSoundSettings';
import { isWorkflowImportDialogOpen } from 'selectors/workflowImport';
import { isWorkflowCreationWizardOpen } from 'selectors/workflowCreationWizard';

const ToastList = lazyLoadNoSpinner(
  () => import(/* webpackChunkName: "ToastList" */ './containers/ToastList'),
);
const UserIntentPrompt = lazyLoadNoSpinner(
  () =>
    import(
      /* webpackChunkName: "UserIntentPrompt" */ './containers/UserIntentPrompt'
    ),
);
const RemainingVoiceCreditsModal = lazyLoadNoSpinner(
  () =>
    import(
      /* webpackChunkName: "RemainingVoiceCreditsModal" */ './containers/RemainingVoiceCreditsModal'
    ),
);

const EventsWidgetSoundSettings = lazyLoadNoSpinner(
  () =>
    import(
      /* webpackChunkName: "EventsWidgetSoundSettings" */ './EventsWidgetSoundSettings'
    ),
);
const CreateUserModal = lazyLoadNoSpinner(
  () =>
    import(
      /* webpackChunkName: "CreateUserModal" */ '../UserListPage/components/CreateUserModal'
    ),
);
const WorkflowImportDialog = lazyLoadNoSpinner(
  () =>
    import(
      /* webpackChunkName: "WorkflowImportDialog" */ 'components/WorkflowImportDialog'
    ),
);
const WorkflowCreationWizard = lazyLoadNoSpinner(
  () =>
    import(
      /* webpackChunkName: "WorkflowCreationWizard" */ 'components/workflowCreationWizard'
    ),
);

export default function ReactRoot({ isEmbedded }) {
  const voiceCredits = useSelector(voiceCreditPercentage);

  // Important Note:
  // Only add components here if you are sure they need to be global.
  // And, if you do add a component, make sure you do it in the form:
  // {shouldShowX && <LazyComponentX />}
  // as lazy defers loading the bundle until rendering, which will result in a better startup time.
  return (
    <div>
      <ToastList isEmbedded={isEmbedded} />
      {!isEmbedded && <UserIntentPrompt />}

      {!isNil(voiceCredits) && (
        <RemainingVoiceCreditsModal voiceCredits={voiceCredits} />
      )}
      {useSelector(isCreateUserModalOpen) && <CreateUserModal />}
      {useSelector(isEventWidgetSoundSettingsShown) && (
        <EventsWidgetSoundSettings />
      )}
      {useSelector(isWorkflowImportDialogOpen) && <WorkflowImportDialog />}
      {useSelector(isWorkflowCreationWizardOpen) && <WorkflowCreationWizard />}
    </div>
  );
}

ReactRoot.displayName = 'ReactRoot';
ReactRoot.propTypes = {
  isEmbedded: PropTypes.bool,
};
ReactRoot.defaultProps = {
  isEmbedded: false,
};
